"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  JWT_STORAGE_KEY,
  buildSynapticIdentityUrl,
  hasAnySynapticRole,
  hasSynapticCapability,
  hasSynapticRole,
  synapticUserFromJwt,
  type SynapticCapability,
  type SynapticRole,
  type SynapticUser,
} from "@/lib/auth";

type AuthContextValue = {
  user: SynapticUser | null;
  token: string | null;
  isAuthenticated: boolean;
  signIn: () => void;
  signUp: () => void;
  logOut: () => void;
  hasRole: (role: SynapticRole) => boolean;
  hasAnyRole: (roles: SynapticRole[]) => boolean;
  can: (capability: SynapticCapability) => boolean;
  canViewEmail: () => boolean;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<SynapticUser | null>(null);

  const loadFromStorage = useCallback(() => {
    const storedToken = window.localStorage.getItem(JWT_STORAGE_KEY);
    const storedUser = storedToken ? synapticUserFromJwt(storedToken) : null;

    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(storedUser);
      return;
    }

    if (storedToken) {
      window.localStorage.removeItem(JWT_STORAGE_KEY);
    }

    setToken(null);
    setUser(null);
  }, []);

  useEffect(() => {
    loadFromStorage();

    const handleStorage = (event: StorageEvent) => {
      if (event.key === JWT_STORAGE_KEY) loadFromStorage();
    };

    window.addEventListener("storage", handleStorage);
    window.addEventListener("synaptic-auth:changed", loadFromStorage);

    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("synaptic-auth:changed", loadFromStorage);
    };
  }, [loadFromStorage]);

  const navigateToIdentity = useCallback((action: "login" | "register") => {
    window.location.assign(buildSynapticIdentityUrl(action, window.location.href));
  }, []);

  const logOut = useCallback(() => {
    window.localStorage.removeItem(JWT_STORAGE_KEY);
    setToken(null);
    setUser(null);
    window.dispatchEvent(new Event("synaptic-auth:changed"));
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      token,
      isAuthenticated: Boolean(user && token),
      signIn: () => navigateToIdentity("login"),
      signUp: () => navigateToIdentity("register"),
      logOut,
      hasRole: (role) => hasSynapticRole(user, role),
      hasAnyRole: (roles) => hasAnySynapticRole(user, roles),
      can: (capability) => hasSynapticCapability(user, capability),
      canViewEmail: () => hasSynapticCapability(user, "canViewEmail"),
    }),
    [logOut, navigateToIdentity, token, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
